/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Icon } from '@fluentui/react';
import { getId } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { LuisSidebar } from 'src/core/components/LuisSidebar';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { LuisSidebarItemProps } from 'src/core/types/luisSidebar';
import { routeTable } from 'src/core/utils/routeTable';
import { doesRouteMatchLocation } from 'src/core/utils/routingUtils';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { qnaMakerTeachingBubbleMessages } from 'src/qnaMaker/core/utils/qnaMakerTeachingBubbleMessages';
import styled from 'styled-components';

const messages = defineMessages({
    home: { id: 'qnaMaker.qnaMakerSidebar.home', defaultMessage: 'Language Studio' },
    qnaProjects: { id: 'qnaMaker.qnaMakerSidebar.qnaProjects', defaultMessage: 'Custom question answering' },
    azureSearch: { id: 'qnaMaker.qnaMakerSidebar.azureSearch', defaultMessage: 'Azure Search' },
    manageSources: { id: 'qnaMaker.qnaMakerSidebar.manageSources', defaultMessage: 'Manage sources' },
    editKB: { id: 'qnaMaker.qnaMakerSidebar.editKB', defaultMessage: 'Edit knowledge base' },
    deployKB: { id: 'qnaMaker.qnaMakerSidebar.deployKB', defaultMessage: 'Deploy knowledge base' },
    reviewSuggestions: { id: 'qnaMaker.qnaMakerSidebar.reviewSuggestions', defaultMessage: 'Review suggestions' },
    projectSettings: { id: 'qnaMaker.qnaMakerSidebar.projectSettings', defaultMessage: 'Project settings' }
});

const theme = getScopedTheme('QnaMakerSidebar');
const YellowDotWithIcon = styled.div`
    background-color: ${theme.yellowDotColor};
    width: 8px;
    height: 8px;
    border-radius: 8px;
    position: absolute;
    right: 6px;
    top: 6px;
`;
const YellowDotWithText = styled.div`
    background-color: ${theme.yellowDotColor};
    width: 12px;
    height: 12px;
    border-radius: 12px;
    position: absolute;
    right: 24px;
    top: 16px;
`;

export const QnaMakerSidebar = observer(() => {
    const history = useHistory();
    const { pathname } = useLocation();
    const { localize } = useLocalization();
    const { rootStore, uiStore } = useGlobalState();
    const editItemIdRef = React.useRef<string>(getId());
    const deployItemIdRef = React.useRef<string>(getId());
    const reviewSuggestionsItemIdRef = React.useRef<string>(getId());
    const projectSettingsItemIdRef = React.useRef<string>(getId());
    const azureSearchIdRef = React.useRef<string>(getId());
    const { isSignedIn } = rootStore.authStore;
    const { currentQnaMakerProjectStore } = rootStore;
    const azureSession = rootStore.azureStore.session;
    const currentProjectName = currentQnaMakerProjectStore?.name;
    const isSidebarVisible = isSignedIn && currentProjectName;
    const isResourceConnectedToAzureSearch =
        Boolean(azureSession.subscriptionId) &&
        Boolean(azureSession.csAccountStore?.path) &&
        azureSession.csAccountStore?.kind === 'TextAnalytics' &&
        Boolean(azureSession.csAccountStore?.ownedSearchResourceId);
    const isInEditPage =
        doesRouteMatchLocation(routeTable.qnaMaker.editPage(currentProjectName), { locationToMatch: pathname }) ||
        doesRouteMatchLocation(routeTable.qnaMaker.detailsPage(currentProjectName), { locationToMatch: pathname });
    const sidebarToggleButtonId = isInEditPage && getId();
    const isSmartSuggestionIndicatorDisplayed =
        currentQnaMakerProjectStore?.isSmartSuggestionEnabled && currentQnaMakerProjectStore?.haveSmartSuggestions;

    React.useEffect(
        () => () => {
            dequeueTeachingBubble('QnaSidebarEdit');
            dequeueTeachingBubble('QnaSidebarDeploy');
            dequeueTeachingBubble('QnaSidebarReviewSuggestion');
            dequeueTeachingBubble('QnaSidebarProjectSettings');
            dequeueTeachingBubble('QnaAzureSearch');
        },
        []
    );

    React.useEffect(() => {
        if (!currentProjectName) {
            dequeueTeachingBubble('QnaSidebarEdit');
            dequeueTeachingBubble('QnaSidebarDeploy');
            dequeueTeachingBubble('QnaSidebarReviewSuggestion');
            dequeueTeachingBubble('QnaSidebarProjectSettings');
        }
    }, [currentProjectName]);

    React.useEffect(() => {
        if (isSidebarVisible && !uiStore.teachingBubbles.QnaAzureSearch && !isResourceConnectedToAzureSearch) {
            createTeachingBubble(
                'QnaAzureSearch',
                localize(qnaMakerTeachingBubbleMessages.azureSearchBody),
                localize(qnaMakerTeachingBubbleMessages.azureSearchTitle),
                `#${azureSearchIdRef.current}`,
                true
            );
        }

        if (!currentProjectName) {
            return;
        }

        if (
            uiStore.teachingBubbles.FirstSourceCreated &&
            !uiStore.teachingBubbles.QnaSidebarEdit &&
            !uiStore.skippedTeachingBubbles.FirstSourceCreated
        ) {
            createTeachingBubble(
                'QnaSidebarEdit',
                localize(qnaMakerTeachingBubbleMessages.sidebarEditBody),
                localize(qnaMakerTeachingBubbleMessages.sidebarEditTitle),
                `#${editItemIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (uiStore.teachingBubbles.QnaFilter && !uiStore.teachingBubbles.QnaSidebarDeploy && !uiStore.skippedTeachingBubbles.QnaFilter) {
            createTeachingBubble(
                'QnaSidebarDeploy',
                localize(qnaMakerTeachingBubbleMessages.sidebarDeployBody),
                localize(qnaMakerTeachingBubbleMessages.sidebarDeployTitle),
                `#${deployItemIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            uiStore.teachingBubbles.QnaCreateBot &&
            !uiStore.teachingBubbles.QnaSidebarReviewSuggestion &&
            !uiStore.skippedTeachingBubbles.QnaCreateBot
        ) {
            createTeachingBubble(
                'QnaSidebarReviewSuggestion',
                localize(qnaMakerTeachingBubbleMessages.sidebarReviewSuggestionBody),
                localize(qnaMakerTeachingBubbleMessages.sidebarReviewSuggestionTitle),
                `#${reviewSuggestionsItemIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            uiStore.teachingBubbles.QnaSidebarDeploy &&
            !uiStore.teachingBubbles.QnaSidebarProjectSettings &&
            !uiStore.skippedTeachingBubbles.QnaSidebarDeploy
        ) {
            createTeachingBubble(
                'QnaSidebarProjectSettings',
                localize(qnaMakerTeachingBubbleMessages.sidebarProjectSettingsBody),
                localize(qnaMakerTeachingBubbleMessages.sidebarProjectSettingsTitle),
                `#${projectSettingsItemIdRef.current}`,
                true
            );
        }

        if (!uiStore.teachingBubbles.QnaSideBarCollapseInEditPage && uiStore.isSidebarCollapsed && isInEditPage) {
            createTeachingBubble(
                'QnaSideBarCollapseInEditPage',
                localize(qnaMakerTeachingBubbleMessages.sidebarCollapsedTitle),
                localize(qnaMakerTeachingBubbleMessages.sidebarCollapsedBody),
                `#${sidebarToggleButtonId}`,
                true
            );
        }
    }, [Object.values(uiStore.teachingBubbles), currentProjectName, uiStore.isSidebarCollapsed]);

    const renderSmartSuggestionsIcon = (props: LuisSidebarItemProps) => (
        <>
            <Icon
                styles={{
                    root: {
                        height: 16
                    }
                }}
                iconName={'CRMCustomerInsightsApp'}
            />
            {isSmartSuggestionIndicatorDisplayed && props.isCollapsed && <YellowDotWithIcon />}
        </>
    );

    const renderReviewSuggestionsText = (props: LuisSidebarItemProps) => {
        const isSelected = doesRouteMatchLocation(routeTable.qnaMaker.reviewPage(currentProjectName), { locationToMatch: pathname });
        const fontWeight = isSelected ? theme.itemLinkFontWeightSelected : theme.itemLinkFontWeight;

        return (
            <>
                <LuisText fontWeight={fontWeight as Property.FontWeight}>{props.text}</LuisText>
                {isSmartSuggestionIndicatorDisplayed && <YellowDotWithText />}
            </>
        );
    };

    const items: LuisSidebarItemProps[] = [
        {
            iconName: 'Home',
            text: localize(messages.home),
            onClick: () => history.push(routeTable.languageStudio.root()),
            isSelected: doesRouteMatchLocation(routeTable.languageStudio.root(), { exact: true, locationToMatch: pathname })
        },
        {
            forceNoWrap: true,
            iconName: 'AlignLeft',
            text: localize(messages.qnaProjects),
            onClick: () => history.push(routeTable.qnaMaker.projectsPage()),
            isSelected: doesRouteMatchLocation(routeTable.qnaMaker.projectsPage(), { exact: true, locationToMatch: pathname })
        },
        {
            iconName: 'Database',
            id: azureSearchIdRef.current,
            text: localize(messages.azureSearch),
            onClick: () => history.push(routeTable.qnaMaker.azureSearchPage()),
            isSelected: doesRouteMatchLocation(routeTable.qnaMaker.azureSearchPage(), { locationToMatch: pathname })
        },
        { isCollapsed: true, itemType: 'Header', text: currentProjectName },
        {
            iconName: 'Source',
            text: localize(messages.manageSources),
            onClick: () => history.push(routeTable.qnaMaker.manageSourcesPage(currentProjectName)),
            isSelected: doesRouteMatchLocation(routeTable.qnaMaker.manageSourcesPage(currentProjectName), {
                locationToMatch: pathname
            })
        },
        {
            isSelected: isInEditPage,
            id: editItemIdRef.current,
            iconName: 'BuildDefinition',
            text: localize(messages.editKB),
            onClick: () => history.push(`${routeTable.qnaMaker.editPage(currentProjectName)}?assets=qnas`)
        },
        {
            iconName: 'Deploy',
            id: deployItemIdRef.current,
            text: localize(messages.deployKB),
            onClick: () => history.push(routeTable.qnaMaker.deployPage(currentProjectName)),
            isSelected: doesRouteMatchLocation(routeTable.qnaMaker.deployPage(currentProjectName), { locationToMatch: pathname })
        },
        {
            iconName: 'CRMCustomerInsightsApp',
            id: reviewSuggestionsItemIdRef.current,
            text: localize(messages.reviewSuggestions),
            onClick: () => history.push(routeTable.qnaMaker.reviewPage(currentProjectName)),
            onRenderIcon: renderSmartSuggestionsIcon,
            onRenderText: renderReviewSuggestionsText,
            isSelected: doesRouteMatchLocation(routeTable.qnaMaker.reviewPage(currentProjectName), { locationToMatch: pathname })
        },
        {
            iconName: 'Settings',
            id: projectSettingsItemIdRef.current,
            text: localize(messages.projectSettings),
            onClick: () => history.push(routeTable.qnaMaker.settingsPage(currentProjectName)),
            isSelected: doesRouteMatchLocation(routeTable.qnaMaker.settingsPage(currentProjectName), { locationToMatch: pathname })
        }
    ];

    return currentProjectName ? <LuisSidebar itemLists={[items]} sidebarToggleButtonId={sidebarToggleButtonId} /> : null;
});
